import { AuthModel, UserModel, UserProducerCenter } from "./_models";
import { GenerateNewToken, GenerateNewTokenV2 } from "./_requests";
import { getDeviceOS, getBrowserName, getUserDeviceInfo } from "../../utils/DeviceHelper";
// import { useAuth } from "./Auth";

const AUTH_LOCAL_STORAGE_KEY = "kt-auth-react-v";
const CURRENT_USER_LOCAL_STORAGE_KEY = "current-user";
const USER_PRODUCER_CENTER_LOCAL_STORAGE_KEY = "user-producer-center";
const tokenExpireValue = process.env.REACT_APP_EXPIRE_TOKEN ?? "480";

// const { saveAuth } = useAuth();

const getAuth = (): AuthModel | undefined => {
  if (!localStorage) {
    return;
  }

  const lsValue: string | null = localStorage.getItem(AUTH_LOCAL_STORAGE_KEY);
  if (!lsValue) {
    return;
  }

  try {
    const auth: AuthModel = JSON.parse(lsValue) as AuthModel;
    if (auth) {
      //check if token is expire request another one with refresh token
      const currentTime = new Date().getTime(); // Get current time in milliseconds
      const tokenExpireMinutes = parseInt(tokenExpireValue);
      const tokenExpire = auth.createDate + tokenExpireMinutes * 60 * 1000;
      if (auth.createDate && currentTime < tokenExpire) {
        // Token is not expired
        return auth;
      } else {
        console.log("Token expired");
        return undefined;
      }
    }
  } catch (error) {
    console.error("AUTH LOCAL STORAGE PARSE ERROR", error);
  }
};

const setAuth = (auth: AuthModel) => {
  if (!localStorage) {
    return;
  }

  try {
    const lsValue = JSON.stringify(auth);
    localStorage.setItem(AUTH_LOCAL_STORAGE_KEY, lsValue);
  } catch (error) {
    console.error("AUTH LOCAL STORAGE SAVE ERROR", error);
  }
};

const removeAuth = () => {
  if (!localStorage) {
    return;
  }

  try {
    localStorage.removeItem(AUTH_LOCAL_STORAGE_KEY);
  } catch (error) {
    console.error("AUTH LOCAL STORAGE REMOVE ERROR", error);
  }
};

export function setupAxios(axios: any) {
  axios.defaults.headers.Accept = "application/json";
  axios.interceptors.request.use(
    (config: { headers: { Authorization: string } }) => {
      const auth = getAuth();
      if (auth && auth.token) {
        config.headers.Authorization = `Bearer ${auth.token}`;
      }

      return config;
    },
    (err: any) => Promise.reject(err)
  );

  let isRefreshing = false;
  let failedQueue: any[] = [];

  const processQueue = (error: any, token: string | null = null) => {
    failedQueue.forEach((prom) => {
      if (token) {
        prom.resolve(token);
      } else {
        prom.reject(error);
      }
    });
    failedQueue = [];
  };

  axios.interceptors.response.use(
    (response: any) => response,
    async (error: any) => {
      const originalRequest = error.config;

      if (error.response && error.response.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;

        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({
              resolve: (token: string) => {
                originalRequest.headers["Authorization"] = `Bearer ${token}`;
                resolve(axios(originalRequest));
              },
              reject: (err: any) => {
                reject(err);
              },
            });
          });
        }

        isRefreshing = true;

        try {
          const auth = getAuth();

          var deviceOS = getDeviceOS()
          var deviceBrowser = getBrowserName()
          var deviceName = getUserDeviceInfo()

          const newAuth = await GenerateNewTokenV2(20, deviceOS, deviceBrowser, deviceName);

          if (newAuth?.data?.isSuccess) {
            const newToken = newAuth.data.data.token;

            const authData: AuthModel = {
              token: newToken,
              refreshToken: "",
              createDate: new Date().getTime(),
            };
            setAuth(authData);

            axios.defaults.headers.common["Authorization"] = `Bearer ${newToken}`;
            processQueue(null, newToken);

            originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
            return axios(originalRequest);
          } else {
            processQueue(new Error("Refresh failed"), null);
            removeCurrentUser();
            removeUserProducerCenter();
            removeAuth();
            window.location.href = "/auth";
            return Promise.reject(error);
          }
        } catch (refreshError) {
          processQueue(refreshError, null);
          removeCurrentUser();
          removeUserProducerCenter();
          removeAuth();
          window.location.href = "/auth";
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }

      return Promise.reject(error);
    }
  );
}

///////////////////////save current user in local storage
const getCurrentUser = (): UserModel | undefined => {
  if (!localStorage) {
    return;
  }

  const lsValue: string | null = localStorage.getItem(
    CURRENT_USER_LOCAL_STORAGE_KEY
  );
  if (!lsValue) {
    return;
  }

  try {
    const currentUser: UserModel = JSON.parse(lsValue) as UserModel;
    if (currentUser) {
      return currentUser;
    }
  } catch (error) {
    console.error("CURRENT USER LOCAL STORAGE PARSE ERROR", error);
  }
};

const setCurrentUser = (currentUser: UserModel) => {
  if (!localStorage) {
    return;
  }

  try {
    const lsValue = JSON.stringify(currentUser);
    localStorage.setItem(CURRENT_USER_LOCAL_STORAGE_KEY, lsValue);
  } catch (error) {
    console.error("CURRENT USER LOCAL STORAGE SAVE ERROR", error);
  }
};

const removeCurrentUser = () => {
  if (!localStorage) {
    return;
  }

  try {
    localStorage.removeItem(CURRENT_USER_LOCAL_STORAGE_KEY);
  } catch (error) {
    console.error("CURRENT USER LOCAL STORAGE REMOVE ERROR", error);
  }
};
//////////////////////

///////////////////////save user producer center in local storage
const getUserProducerCenter = (): UserProducerCenter | undefined => {
  if (!localStorage) {
    return;
  }

  const lsValue: string | null = localStorage.getItem(
    USER_PRODUCER_CENTER_LOCAL_STORAGE_KEY
  );
  if (!lsValue) {
    return;
  }

  try {
    const userProducerCenter: UserProducerCenter = JSON.parse(
      lsValue
    ) as UserProducerCenter;
    if (userProducerCenter) {
      return userProducerCenter;
    }
  } catch (error) {
    console.error("USER PRODUCER CENTER LOCAL STORAGE PARSE ERROR", error);
  }
};

const setUserProducerCenter = (userProducerCenter: UserProducerCenter) => {
  if (!localStorage) {
    return;
  }

  try {
    const lsValue = JSON.stringify(userProducerCenter);
    localStorage.setItem(USER_PRODUCER_CENTER_LOCAL_STORAGE_KEY, lsValue);
  } catch (error) {
    console.error("USER PRODUCER CENTER LOCAL STORAGE SAVE ERROR", error);
  }
};

const removeUserProducerCenter = () => {
  if (!localStorage) {
    return;
  }

  try {
    localStorage.removeItem(USER_PRODUCER_CENTER_LOCAL_STORAGE_KEY);
  } catch (error) {
    console.error("USER PRODUCER CENTER LOCAL STORAGE REMOVE ERROR", error);
  }
};
//////////////////////

export {
  getAuth,
  setAuth,
  removeAuth,
  AUTH_LOCAL_STORAGE_KEY,
  getCurrentUser,
  setCurrentUser,
  removeCurrentUser,
  CURRENT_USER_LOCAL_STORAGE_KEY,
  getUserProducerCenter,
  setUserProducerCenter,
  removeUserProducerCenter,
  USER_PRODUCER_CENTER_LOCAL_STORAGE_KEY,
};
